import { useEffect, useState } from "react";
import { Database, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { nf } from "./shared";
import { ConsumiStati } from "./ConsumiStati";

function PubToggle() {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/monitoring/consumi-pubblicazione").then((r) => setP(r.data.pubblicati)).catch(() => {}); }, []);
  const set = (v) => api.put("/monitoring/consumi-pubblicazione", { pubblicati: v }).then((r) => { setP(r.data.pubblicati); toast.success(v ? "Consumi VESTA pubblicati" : "Consumi VESTA nascosti agli utenti"); }).catch((e) => toast.error(e.response?.data?.detail || "Non consentito"));
  if (p === null) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-neutral-800 p-3 text-xs" data-testid="consumi-pubblicazione">
      <span className="text-neutral-400">Consumi VESTA (nuove regole): <b className={p ? "text-emerald-400" : "text-amber-400"}>{p ? "pubblicati" : "non pubblicati, visibili solo allo staff in anteprima"}</b></span>
      <button type="button" onClick={() => api.post("/monitoring/audit/avvia").then(() => toast.success("Analisi completa avviata: richiede circa 2 ore"))} data-testid="audit-avvia" className="rounded-full border border-neutral-700 px-3 py-1 text-neutral-200">Analizza tutti gli impianti</button>
      <button type="button" onClick={() => set(!p)} data-testid="consumi-pubblicazione-toggle" className="ml-auto rounded-full border border-neutral-700 px-3 py-1 text-neutral-200">{p ? "Nascondi" : "Pubblica"}</button>
    </div>
  );
}

function Avanzamento({ a }) {
  if (!a) return null;
  const fine = a.percentuale >= 100;
  const data = a.previsto ? new Date(a.previsto).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : null;
  return (
    <div className="space-y-2 rounded-xl border border-neutral-800 p-3 text-xs" data-testid="storico-progress">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-neutral-400">Storico consumi</span>
        <b className="font-mono text-base text-white" data-testid="storico-progress-pct">{nf(a.percentuale, 1)}% recuperato</b>
        <span className="text-neutral-500" data-testid="storico-progress-eta">{fine ? "· completo" : data ? `· completo previsto il ${data}` : "· data prevista disponibile dopo qualche giro"}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-neutral-800"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${Math.min(a.percentuale, 100)}%` }} /></div>
      <div className="text-neutral-500">{nf(a.giorni_fatti, 0)} di {nf(a.giorni_totali, 0)} giorni-impianto{a.al_giorno ? ` · circa ${nf(a.al_giorno, 0)} al giorno` : ""}</div>
    </div>
  );
}

function Anomalie({ onOpen }) {
  const [l, setL] = useState(null);
  useEffect(() => { api.get("/monitoring/consumi-anomalie", { timeout: 60000 }).then((r) => setL(r.data.anomalie)).catch(() => setL([])); }, []);
  if (l === null) return <Loader2 className="mx-auto h-5 w-5 animate-spin text-neutral-600" />;
  return (
    <div className="rounded-xl border border-neutral-800 p-3 text-xs" data-testid="consumi-anomalie">
      <div className="mb-2 text-neutral-400">Anomalie consumi ultimi 7 giorni <b className="text-amber-400">{l.length}</b> <span className="text-neutral-600">(visibili solo allo staff: ai clienti i dati restano come misurati)</span></div>
      <div className="max-h-64 divide-y divide-neutral-900 overflow-y-auto">
        {l.map((a) => (
          <button key={a.id} type="button" onClick={() => onOpen(a.id)} data-testid={`anomalia-${a.id}`} className="block w-full px-1 py-2 text-left hover:bg-white/5">
            <b className="text-white">{a.nome}</b> <span className="text-neutral-500">{a.kwp ? `${nf(a.kwp)} kWp` : ""}</span>
            <span className="block text-amber-400">{a.motivi.join(" · ")}</span>
          </button>
        ))}
        {!l.length && <p className="py-2 text-neutral-500">Nessuna anomalia rilevata.</p>}
      </div>
    </div>
  );
}

export function QualitaDati({ onOpenPlant }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const load = () => { setD(null); api.get("/monitoring/qualita").then((r) => setD(r.data)).catch(() => setD({ righe: [], errore: true })); };
  const avvia = () => api.post("/monitoring/archivio/avvia").then(() => toast.success("Archiviazione avviata: prosegue in automatico ogni 15 minuti")).catch(() => toast.error("Avvio non riuscito"));
  return (
    <>
      <button type="button" onClick={() => { setOpen(true); load(); }} data-testid="monitoring-qualita-btn"
        className="inline-flex items-center gap-2 rounded-full border border-neutral-800 px-4 py-2 text-xs font-semibold text-neutral-300 transition-colors hover:border-neutral-500 hover:text-white">
        <Database className="h-4 w-4" />Qualità dati e storico
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="monitoring-qualita-dialog">
          <DialogHeader><DialogTitle>Qualità dati e storico</DialogTitle></DialogHeader>
          {!d ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-neutral-500" /> : (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-3 gap-2 text-xs" data-testid="monitoring-qualita-summary">
                <div className="rounded-xl border border-neutral-800 p-3"><div className="text-neutral-500">Giorni archiviati</div><b className="font-mono text-lg">{nf(d.giorni_archiviati, 0)}</b></div>
                <div className="rounded-xl border border-neutral-800 p-3"><div className="text-neutral-500">Storico recuperato</div><b className="font-mono text-lg">{d.storico_completo || 0} / {d.impianti || 0}</b></div>
                <div className="rounded-xl border border-neutral-800 p-3"><div className="text-neutral-500">Impianti da verificare</div><b className="font-mono text-lg text-amber-400">{d.righe.length}</b></div>
              </div>
              <PubToggle />
              <Anomalie onOpen={(id) => { setOpen(false); onOpenPlant?.(id); }} />
              <ConsumiStati list={d.consumi} onOpen={(id) => { setOpen(false); onOpenPlant?.(id); }} />
              <Avanzamento a={d.avanzamento} />
              <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500" data-testid="archivio-avanzamento">
                <span>{d.stato?.at ? `Ultimo giro ${new Date(d.stato.at).toLocaleString("it-IT")} · ${d.stato.giorni_ultimo_giro ?? 0} giorni salvati` : "Primo giro non ancora eseguito"} · storico completo {d.storico_completo || 0}, in recupero {d.in_recupero || 0}, da completare {Math.max((d.impianti || 0) - (d.storico_completo || 0), 0)}</span>
                <button type="button" onClick={avvia} data-testid="monitoring-archivio-avvia" className="rounded-full border border-neutral-700 px-3 py-1 font-semibold text-neutral-200 hover:border-neutral-400">Avvia ora</button>
              </div>
              <div className="divide-y divide-neutral-900 rounded-xl border border-neutral-800">
                {d.righe.map((r) => (
                  <button key={r.id} type="button" onClick={() => { setOpen(false); onOpenPlant?.(r.id); }} data-testid={`qualita-row-${r.id}`}
                    className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-left text-xs transition-colors hover:bg-white/5">
                    <b className="text-white">{r.nome}</b><span className="text-neutral-500">{r.partner} · {nf(r.kwp)} kWp</span>
                    {r.completezza_7g != null && <span className="text-neutral-400">dati 7 gg {r.completezza_7g}%</span>}
                    <span className="w-full text-amber-400">{r.problemi.join(" · ")}</span>
                  </button>
                ))}
                {!d.righe.length && <p className="p-4 text-center text-xs text-neutral-500">Nessun problema rilevato.</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
